import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { UploadFile, VerifiedUser, Block, FactCheck, WorkspacePremium } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { storage } from '../../firebase';
import { makeApiCall, getAuthHeaders } from '../../utils/apiConfig';
import { DateField, StatusChip } from '../ui';
import { ValidatedTextField } from '../ui/ValidatedTextField';
import { validateEmail } from '../../utils/inputValidators';
import {
  BrandAuthorization,
  accountSummary,
  methodLabel,
  VERIFICATION_STATUS_META,
  channelsLabel,
  levelLabel,
  proofStoragePath,
  verificationStatusOf,
  verificationTagOf,
} from '../../utils/brandAuthorization';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { brandAuthorizationTagCopy } from './BrandAuthorizationTagChip';
import BrandDesignationChips from './BrandDesignationChips';
import BrandDesignationsDialog from './BrandDesignationsDialog';

interface AdminBrandAuthorizationsPanelProps {
  organizationId: string;
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    return body?.message || body?.error?.message || fallback;
  } catch {
    return fallback;
  }
}

/**
 * Alex's view of one store's brand authorizations, inside Store Details.
 * Covers the admin-assisted path: a dealer emails proof, Alex uploads it
 * here and verifies the brand on their behalf, recording the sender, and the
 * one-click approval of a dealer-list match Motzu did not verify on its own.
 */
const AdminBrandAuthorizationsPanel: React.FC<AdminBrandAuthorizationsPanelProps> = ({ organizationId }) => {
  const queryClient = useQueryClient();
  const { t } = useAppTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [verifyTarget, setVerifyTarget] = useState<BrandAuthorization | null>(null);
  const [rejectTarget, setRejectTarget] = useState<BrandAuthorization | null>(null);
  const [designationsTarget, setDesignationsTarget] = useState<BrandAuthorization | null>(null);
  const [sourceEmail, setSourceEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queryKey = ['admin-brand-authorizations', organizationId];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await makeApiCall(`/admin/brand-authorizations/organizations/${organizationId}`, {
        headers: await getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await readError(response, 'Failed to load brand authorizations'));
      return response.json() as Promise<{ data: BrandAuthorization[]; proofGating: boolean }>;
    },
    enabled: !!organizationId,
  });

  const reset = () => {
    setVerifyTarget(null);
    setRejectTarget(null);
    setSourceEmail('');
    setExpiresAt('');
    setNotes('');
    setFile(null);
    setError(null);
  };

  const verify = useMutation({
    mutationFn: async (record: BrandAuthorization) => {
      // Validate before touching Storage so a bad form never leaves an orphaned file behind.
      const adminAssisted = Boolean(file) || Boolean(sourceEmail.trim());
      if (adminAssisted && !sourceEmail.trim()) {
        throw new Error('Enter the email address the dealer sent the proof from');
      }
      const headers = { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };
      let proof: { fileName: string; storagePath: string; contentType?: string } | undefined;
      if (file) {
        const path = proofStoragePath(organizationId, record.brandId, file.name);
        await uploadBytes(storageRef(storage, path), file, { contentType: file.type || 'application/pdf' });
        proof = { fileName: file.name, storagePath: path, contentType: file.type || undefined };
      }
      const response = await makeApiCall(
        `/admin/brand-authorizations/organizations/${organizationId}/${encodeURIComponent(record.brandId)}/verify`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            method: adminAssisted ? 'admin_assisted' : 'dealer_letter',
            sourceEmail: sourceEmail.trim() || undefined,
            expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
            notes: notes.trim() || undefined,
            proof,
          }),
        },
      );
      if (!response.ok) throw new Error(await readError(response, 'Failed to verify'));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['admin-store-details'] });
      reset();
    },
    onError: (e: any) => setError(e?.message || 'Failed to verify'),
  });

  // Approve a list match Motzu recorded but did not verify (partial spelling,
  // older list, or held after an identity change): the list is the evidence.
  const confirmFromList = useMutation({
    mutationFn: async (record: BrandAuthorization) => {
      const response = await makeApiCall(
        `/admin/brand-authorizations/organizations/${organizationId}/${encodeURIComponent(record.brandId)}/approve-list-match`,
        {
          method: 'POST',
          headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        },
      );
      if (!response.ok) throw new Error(await readError(response, 'Failed to approve the list match'));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['admin-store-details'] });
    },
    onError: (e: any) => setError(e?.message || 'Failed to verify'),
  });

  const checkLists = useMutation({
    mutationFn: async () => {
      const response = await makeApiCall(`/admin/brand-dealer-directory/check/${organizationId}`, {
        method: 'POST',
        headers: await getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await readError(response, 'Failed to check dealer lists'));
      return response.json() as Promise<{ data: Array<{ brandId: string; confidence: string; verified: boolean }> }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['admin-store-details'] });
    },
    onError: (e: any) => setError(e?.message || 'Failed to check dealer lists'),
  });

  const reject = useMutation({
    mutationFn: async (record: BrandAuthorization) => {
      if (!notes.trim()) throw new Error('Tell the dealer why the proof was rejected');
      const response = await makeApiCall(
        `/admin/brand-authorizations/organizations/${organizationId}/${encodeURIComponent(record.brandId)}/reject`,
        {
          method: 'POST',
          headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes: notes.trim() }),
        },
      );
      if (!response.ok) throw new Error(await readError(response, 'Failed to reject'));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      reset();
    },
    onError: (e: any) => setError(e?.message || 'Failed to reject'),
  });

  const records = data?.data || [];

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Brand authorizations
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {data && (
            <Chip
              size="small"
              variant="outlined"
              label={data.proofGating ? 'Proof gating on: unverified brands are locked' : 'Proof gating off'}
            />
          )}
          <Button size="small" variant="outlined" startIcon={<FactCheck />} disabled={checkLists.isPending} onClick={() => checkLists.mutate()}>
            {checkLists.isPending ? 'Checking…' : 'Check dealer lists'}
          </Button>
        </Box>
      </Box>
      {error && !verifyTarget && !rejectTarget && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>
      )}
      {isLoading ? (
        <CircularProgress size={20} />
      ) : records.length === 0 ? (
        <Typography variant="body2" color="text.secondary">This store has not listed any brands.</Typography>
      ) : (
        <Stack spacing={1}>
          {records.map((record) => {
            const status = verificationStatusOf(record);
            const meta = VERIFICATION_STATUS_META[status];
            const tag = verificationTagOf(record);
            const tagCopy = brandAuthorizationTagCopy(t, record);
            const account = accountSummary(record);
            return (
              <Box
                key={record.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  p: 1.25,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{record.brandName}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {levelLabel(record.level)} · {channelsLabel(record.channels)}
                    {account ? ` · ${account}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {methodLabel(record.verification.method)}
                    {record.verification.sourceEmail ? ` (${record.verification.sourceEmail})` : ''}
                    {record.proof ? (
                      <>
                        {' '}· {record.proof.url ? <a href={record.proof.url} target="_blank" rel="noreferrer">{record.proof.fileName}</a> : record.proof.fileName}
                      </>
                    ) : record.proofLink ? '' : ' · no proof on file'}
                    {record.proofLink && (
                      <>
                        {' '}· <a href={record.proofLink} target="_blank" rel="noreferrer">proof link</a>
                      </>
                    )}
                    {record.verification.expiresAt ? ` · expires ${new Date(record.verification.expiresAt).toLocaleDateString()}` : ''}
                  </Typography>
                  {record.verification.reviewNotes && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Review: {record.verification.reviewNotes}
                    </Typography>
                  )}
                  {record.proofNote && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Store's note: {record.proofNote}
                    </Typography>
                  )}
                  {record.directoryMatch && record.directoryMatch.confidence !== 'none' && (
                    <Typography variant="caption" sx={{ display: 'block', color: record.directoryMatch.confidence === 'weak' ? 'warning.main' : 'success.main' }}>
                      {record.directoryMatch.confidence === 'weak' ? 'Possible match' : 'Found'} on the {record.brandName} dealer list
                      {record.directoryMatch.listDate ? ` dated ${record.directoryMatch.listDate}` : ''}: {record.directoryMatch.dealerName}
                      {record.directoryMatch.city ? `, ${record.directoryMatch.city}` : ''}{record.directoryMatch.state ? ` ${record.directoryMatch.state}` : ''}
                      {(record.directoryMatch.dealerTypes?.length || record.directoryMatch.dealerType)
                        ? ` (${record.directoryMatch.dealerTypes?.length ? record.directoryMatch.dealerTypes.join(', ') : record.directoryMatch.dealerType})`
                        : ''}
                    </Typography>
                  )}
                  {record.verification.staleListMatch && status !== 'verified' && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                      Found on an older {record.brandName} dealer list
                      {record.verification.staleListMatch.listAge ? ` (${record.verification.staleListMatch.listAge} days old)` : ''}:{' '}
                      {record.verification.staleListMatch.dealerName || 'unnamed row'}
                      {record.verification.staleListMatch.city ? `, ${record.verification.staleListMatch.city}` : ''}
                      {record.verification.staleListMatch.state ? ` ${record.verification.staleListMatch.state}` : ''}
                    </Typography>
                  )}
                  {record.designations && record.designations.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      <BrandDesignationChips designations={record.designations} />
                    </Box>
                  )}
                  {record.directoryMatch && record.directoryMatch.confidence === 'none' && record.directoryMatch.checkedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Not on the {record.brandName} dealer list as of the last check
                    </Typography>
                  )}
                </Box>
                <StatusChip label={meta.label} tone={meta.tone} size="small" />
                {tagCopy && tag.kind !== 'verified' && (
                  <StatusChip label={`Store sees: ${tagCopy.label}`} tone={tagCopy.tone} size="small" variant="outlined" />
                )}
                {tag.kind === 'list_match' && (
                  <Button size="small" variant="contained" disabled={confirmFromList.isPending} onClick={() => { setError(null); confirmFromList.mutate(record); }}>
                    Approve list match
                  </Button>
                )}
                <Button size="small" variant="outlined" startIcon={<VerifiedUser />} onClick={() => { reset(); setVerifyTarget(record); }}>
                  {status === 'verified' ? 'Re-verify' : 'Verify'}
                </Button>
                <Button size="small" variant="text" startIcon={<WorkspacePremium />} onClick={() => setDesignationsTarget(record)}>
                  Designations
                </Button>
                {status !== 'rejected' && (
                  <Button size="small" color="error" startIcon={<Block />} onClick={() => { reset(); setRejectTarget(record); }}>
                    Reject
                  </Button>
                )}
              </Box>
            );
          })}
        </Stack>
      )}

      <Dialog open={!!verifyTarget} onClose={reset} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Verify {verifyTarget?.brandName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {verifyTarget?.proof
                ? 'The dealer sent proof. Confirm it, or attach the proof they emailed instead.'
                : 'No proof on file. Attach the proof the dealer emailed and note the sender to verify on their behalf.'}
            </Typography>
            <Button component="label" variant="outlined" size="small" startIcon={<UploadFile />} sx={{ alignSelf: 'flex-start' }}>
              {file ? file.name : 'Attach emailed proof (PDF or image)'}
              <input hidden type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </Button>
            <ValidatedTextField
              label="Dealer's email (sender of the proof)"
              validate={validateEmail}
              value={sourceEmail}
              onChange={(e) => setSourceEmail(e.target.value)}
              helperText="Required when Motzu uploads the proof on the dealer's behalf"
              fullWidth
            />
            <DateField
              label="Authorization expires"
              value={expiresAt}
              onChange={setExpiresAt}
              helperText="Leave blank if the authorization has no end date"
              size="medium"
              sx={{ width: '100%' }}
            />
            <TextField label="Review notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} multiline rows={2} fullWidth />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={reset}>Cancel</Button>
          <Button variant="contained" disabled={verify.isPending} onClick={() => verifyTarget && verify.mutate(verifyTarget)}>
            {verify.isPending ? <CircularProgress size={18} /> : 'Mark verified'}
          </Button>
        </DialogActions>
      </Dialog>

      <BrandDesignationsDialog
        organizationId={organizationId}
        record={designationsTarget}
        onClose={() => setDesignationsTarget(null)}
        onSaved={() => {
          setDesignationsTarget(null);
          queryClient.invalidateQueries({ queryKey });
          queryClient.invalidateQueries({ queryKey: ['admin-brand-authorizations-queue'] });
        }}
      />

      <Dialog open={!!rejectTarget} onClose={reset} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Reject proof for {rejectTarget?.brandName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Reason the dealer will see"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
              required
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={reset}>Cancel</Button>
          <Button variant="contained" color="error" disabled={reject.isPending} onClick={() => rejectTarget && reject.mutate(rejectTarget)}>
            {reject.isPending ? <CircularProgress size={18} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminBrandAuthorizationsPanel;
